import { withErrorBoundary, Spinner, NoResults } from 'shared'
import { MissingImage } from 'customizations'
import classNames from 'classnames'
import { absolutizePath } from 'shared/util'
import ParkCardBadge from './ParkCardBadge'
import ParkReservationSlots from './ParkReservationSlots'
import { useEffect, useRef, useState, useMemo } from 'react';
import { SmartButtonLink } from 'ui/smart-buttons'
import ParkReservationTimeSlot from 'api/park-reservation-time-slot'

function ParkCard({ park, base = park.baseCollectionName, selectedDate }) {
  const [timeSlots, setTimeSlots] = useState([])
  const [loading, setLoading] = useState(true)
  const timeSlotsRequestRef = useRef(null)

  let isAdminCalander = application.path.includes('/admin-places-calendar')
  let isCommunityPage = application.path.includes('/community-calendar') ||
    application.path.includes('/places-calendar') ||
    application.path.includes('/embed-calendar') ||
    application.path.includes('/admin-places-calendar');

  let isAdminCommunityPage = application.path.includes('/admin-places-calendar');

  const date = useMemo(() => moment(selectedDate || application.todayDateString), [selectedDate]);
  const dateKey = useMemo(() => date.format('YYYY-MM-DD'), [date]);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      const requestKey = `${park.id}-${dateKey}`;
      timeSlotsRequestRef.current = requestKey;
      try {
        let data;
        if (isCommunityPage) {
          data = application.path.includes('/admin-places-calendar')
            ? await ParkApi.loadAdminTimeSlotsForDate(park, date, null)
            : await ParkApi.loadTimeSlotsForDate(park, date, null);
        }
        if (timeSlotsRequestRef.current !== requestKey) return;
        setTimeSlots(data || []);
      } catch (error) {
        if (timeSlotsRequestRef.current !== requestKey) return;
        console.error("Failed to fetch time slots:", error);
        setTimeSlots([]);
      } finally {
        if (timeSlotsRequestRef.current === requestKey)
          setLoading(false);
      }
    };

    fetchData();
  }, [isCommunityPage, park.id, dateKey]);

  const renderItem = (slot) => {
    let { time, reservation, available, militaryTime } = slot
    let options = { }
    let res = reservation
    if (isAdminCalander) {
      res = slot.reservation_admin_view
    }
    if (res) {
      if(slot.disabled) {
        options = {
          text: (
            <>
              Disabled: {slot.desc}
              {isAdminCalander && res.user_name && <br />}{isAdminCalander && res.user_name}
              {isAdminCalander && res.note && <br />}{isAdminCalander && res.note}
            </>
          ),
          className: classNames( 'btn-sm park-reservation-btn', { gray: true }),
          key: time,
        }
      }else {
        options = {
          text: (
            <>
              Cancel: {slot.desc}
              {isAdminCalander && res.user_name && <br />}{isAdminCalander && res.user_name}
              {isAdminCalander && res.note && <br />}{isAdminCalander && res.note}
            </>
          ),
          className: classNames( 'btn-sm park-reservation-btn', { gray: true }),
          key: time,
        }
      }
    } else {
      options = {
        text: (
          <>
            {available ? "Open: ": "Reserved: " }{slot.desc}<br />
            {slot.res_user_name && <br />}{slot.res_user_name}
             {slot.res_note && <br />}{slot.res_note}
          </>
        ),
        className: classNames('btn-sm park-reservation-btn', { gray: available? false : true }),
        key: time,
      };
    }

    let url = park.uid
      ? `${absolutizePath(base)}/${park.uid}?start_date=${date.format("YYYY-MM-DD")}`
      : `${absolutizePath(base)}/park:${park.id}?start_date=${date.format("YYYY-MM-DD")}`;

    return <SmartButtonLink to={url} {...options} click={null} />;
  };

  useEffect(() => {
    async function fetchData() {
      const data = await ParkApi.loadTimeSlotsForDate(park, date, null);
      if (JSON.stringify(data) !== JSON.stringify(timeSlots)) {
        setTimeSlots(data);
      }
    }
    fetchData();
  }, [date, park]);

  return (
    <custom-card className={classNames('ParkCard', park.has_events && 'with-events')}>
      <Link to={park.uid
          ? `${absolutizePath(base)}/${park.uid}?start_date=${date.format("YYYY-MM-DD")}`
          : `${absolutizePath(base)}/park:${park.id}?start_date=${date.format("YYYY-MM-DD")}`
        } className="plain-link">

        <div className="card">
          <div className="card-img-container">
            <img
              className="card-img-top"
              src={park.image_preview_url || MissingImage}
              alt={`${park.name} (ID: ${park.id})`}
            />
            <ParkCardBadge park={park} />
          </div>
          <div className="card-meta">
            {park.isFeatureEnabled('opening') && <div className="card-note float-right">{park.opening_status_str}</div>}
            <h5 className="card-heading">{park.name}</h5>
          </div>
        </div>
      </Link>
      {isCommunityPage && (
        <div className="park-reservation-time-slots">
          {loading ? (
            <Spinner />
          ) : (timeSlots || []).length ? (
            timeSlots.map((ts) => renderItem(ts))
          ) : (
            isAdminCommunityPage ?
              <NoResults className="compact">No Timetables Enabled</NoResults>
              : <NoResults className="compact">No Availability Today</NoResults>
          )}
        </div>
      )}
    </custom-card>
  )
}

export default withErrorBoundary(ParkCard)
